import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { hasReadPermission } from '@/lib/rbac';
import { fetchRecentActivity, type AuditLog } from '@/services/audit';
import { fetchRejectedPaymentAlerts, fetchReconciliationAlerts, type RejectedPaymentAlert, type ReconciliationAlert } from '@/services/alerts';
import ChangePasswordModal from '@/components/feature/ChangePasswordModal';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  icon: string;
  path: string;
  section?: string;
  module: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/admin', module: 'dashboard' },
  { label: 'Pedidos', icon: 'ri-shopping-bag-3-line', path: '/admin/pedidos', section: 'Operaciones', module: 'orders' },
  { label: 'Producción', icon: 'ri-tools-line', path: '/admin/produccion', module: 'produccion' },
  { label: 'Ventas', icon: 'ri-money-dollar-circle-line', path: '/admin/ventas', module: 'sales' },
  { label: 'Clientes', icon: 'ri-user-heart-line', path: '/admin/clientes', module: 'customers' },
  { label: 'Productos', icon: 'ri-archive-line', path: '/admin/productos', section: 'Catálogo', module: 'products' },
  { label: 'Tallas', icon: 'ri-ruler-line', path: '/admin/tallas', module: 'sizes' },
  { label: 'Inventario', icon: 'ri-stack-line', path: '/admin/inventario', section: 'Inventario', module: 'inventory' },
  { label: 'Movimientos', icon: 'ri-exchange-line', path: '/admin/movimientos', module: 'inventory_movements' },
  { label: 'Compras', icon: 'ri-shopping-cart-2-line', path: '/admin/compras', module: 'purchases' },
  { label: 'Proveedores', icon: 'ri-truck-line', path: '/admin/proveedores', module: 'suppliers' },
  { label: 'Distribuidores', icon: 'ri-store-2-line', path: '/admin/distribuidores', section: 'Canales', module: 'distributors' },
  { label: 'Mercancía en consignación', icon: 'ri-git-repository-line', path: '/admin/consignaciones', module: 'consignments' },
  { label: 'Vendedores', icon: 'ri-team-line', path: '/admin/vendedores', module: 'salespersons' },
  { label: 'Comisiones', icon: 'ri-percent-line', path: '/admin/comisiones', module: 'commissions' },
  { label: 'Pagos', icon: 'ri-bank-card-line', path: '/admin/pagos', section: 'Finanzas', module: 'payments' },
  { label: 'Gastos', icon: 'ri-bill-line', path: '/admin/gastos', section: 'Finanzas', module: 'expenses' },
  { label: 'Donaciones', icon: 'ri-heart-line', path: '/admin/donaciones', section: 'Finanzas', module: 'donations' },
  { label: 'Descuentos', icon: 'ri-percent-line', path: '/admin/descuentos', section: 'Finanzas', module: 'discounts' },
  { label: 'Devoluciones', icon: 'ri-arrow-go-back-line', path: '/admin/devoluciones', section: 'Post-venta', module: 'returns' },
  { label: 'Cambios', icon: 'ri-arrow-left-right-line', path: '/admin/cambios', module: 'exchanges' },
  { label: 'Garantías', icon: 'ri-shield-check-line', path: '/admin/garantias', module: 'warranties' },
  { label: 'Marketing', icon: 'ri-megaphone-line', path: '/admin/marketing', section: 'Análisis', module: 'campaigns' },
  { label: 'Embudo en vivo', icon: 'ri-filter-3-line', path: '/admin/embudo', module: 'campaigns' },
  { label: 'Reportes', icon: 'ri-file-chart-line', path: '/admin/reportes', module: 'reports' },
  { label: 'Documentos', icon: 'ri-file-list-3-line', path: '/admin/documentos', module: 'documents' },
  { label: 'Usuarios', icon: 'ri-shield-user-line', path: '/admin/usuarios', section: 'Sistema', module: 'users' },
  { label: 'Auditoría', icon: 'ri-history-line', path: '/admin/auditoria', module: 'audit' },
  { label: 'Reinicio de datos', icon: 'ri-restart-line', path: '/admin/reinicio', module: 'reset' },
  { label: 'Configuración', icon: 'ri-settings-3-line', path: '/admin/configuracion', module: 'settings' },
  { label: 'Preguntas Frecuentes', icon: 'ri-question-answer-line', path: '/admin/faq', module: 'faqs' },
  { label: 'Testimonios de la Manada', icon: 'ri-quote-text', path: '/admin/testimonios', module: 'settings' },
];

const ACTION_VERBS: Record<string, string> = {
  CREATE: 'creó',
  UPDATE: 'editó',
  DELETE: 'eliminó',
};

function actionVerb(action: string): string {
  return ACTION_VERBS[action] ?? 'modificó';
}

function entityShortLabel(type: string): string {
  const map: Record<string, string> = {
    products: 'producto',
    orders: 'pedido',
    sales: 'venta',
    inventory: 'inventario',
    settings: 'configuración',
    consignments: 'consignación',
    customers: 'cliente',
    distributors: 'distribuidor',
    suppliers: 'proveedor',
    purchases: 'compra',
    expenses: 'gasto',
    payments: 'pago',
    commissions: 'comisión',
    donations: 'donación',
    returns: 'devolución',
    exchanges: 'cambio',
    warranties: 'garantía',
    campaigns: 'campaña',
    documents: 'documento',
    sizes: 'talla',
    users: 'usuario',
    salespersons: 'vendedor',
    roles: 'rol',
  };
  return map[type] ?? type;
}

function formatRelative(value: string): string {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const permissions = profile?.permissions ?? {};
  const role = profile?.role ?? '';
  const visibleNavItems = navItems.filter((item) => hasReadPermission(permissions, item.module, role));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [activities, setActivities] = useState<AuditLog[]>([]);
  const [financingAlerts, setFinancingAlerts] = useState<RejectedPaymentAlert[]>([]);
  const [reconciliationAlerts, setReconciliationAlerts] = useState<ReconciliationAlert[]>([]);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // El menú lateral vuelve a su parte de arriba al navegar porque todo el panel
  // se remonta en cada cambio de ruta. Guardamos la posición del scroll del
  // menú y la restauramos al montar, para que no salte de vuelta arriba.
  const SIDEBAR_SCROLL_KEY = 'pcp-sidebar-scroll';
  useLayoutEffect(() => {
    const saved = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY) || '0');
    if (navRef.current && saved > 0) navRef.current.scrollTop = saved;
  }, []);

  const displayName = profile?.fullName || user?.email?.split('@')[0] || 'Administrador';
  const userInitial = (profile?.fullName?.[0] || user?.email?.[0] || 'A').toUpperCase();

  async function handleLogout() {
    await signOut();
    navigate('/login');
  }

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Alerta proactiva: pagos rechazados por la pasarela (Sistecrédito directo y
  // ePayco en la tienda). Se carga al entrar al panel y se refresca cada vez que
  // se abre la campana.
  useEffect(() => {
    let cancelled = false;
    fetchRejectedPaymentAlerts().then((rows) => {
      if (!cancelled) setFinancingAlerts(rows);
    });
    fetchReconciliationAlerts().then((rows) => {
      if (!cancelled) setReconciliationAlerts(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [notifOpen]);

  // Carga las 3 actividades más recientes al abrir la campanita.
  useEffect(() => {
    if (!notifOpen) return;
    let cancelled = false;
    fetchRecentActivity(3).then((logs) => {
      if (!cancelled) setActivities(logs);
    });
    return () => {
      cancelled = true;
    };
  }, [notifOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node) && notifOpen) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node) && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const currentSection = visibleNavItems.find(item => item.section && location.pathname.startsWith(item.path))?.section;

  return (
    <div className="flex h-screen bg-background-50 overflow-hidden">
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-background-200 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <Link to="/admin" className="flex items-center gap-3 px-5 py-5 border-b border-background-100">
          <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-heart-pulse-line text-white text-lg"></i>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground-950 truncate">PCP Admin</p>
            <p className="text-xs text-foreground-600 truncate">Paticas con Propósito</p>
          </div>
        </Link>

        {/* Navigation */}
        <nav
          ref={navRef}
          onScroll={() => {
            if (navRef.current) {
              sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(navRef.current.scrollTop));
            }
          }}
          className="flex-1 overflow-y-auto py-3 px-3"
        >
          {visibleNavItems.reduce<React.ReactNode[]>((acc, item, index) => {
            const prevItem = index > 0 ? visibleNavItems[index - 1] : null;

            if (item.section && (!prevItem || prevItem.section !== item.section)) {
              acc.push(
                <div key={`section-${item.section}`} className="mt-4 mb-1 first:mt-0">
                  <div className="flex items-center justify-between px-3 cursor-pointer" onClick={() => setActiveGroup(activeGroup === item.section ? null : item.section || null)}>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400">{item.section}</span>
                    <i className={`text-foreground-400 text-xs transition-transform ${activeGroup === item.section ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}></i>
                  </div>
                </div>
              );
            }

            const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
            const isHidden = item.section && activeGroup !== item.section && activeGroup !== null;

            acc.push(
              <Link
                key={item.label}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 group ${isHidden ? 'hidden' : ''} ${isActive ? 'bg-accent-100 text-accent-800 font-medium' : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'}`}
              >
                <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${isActive ? '' : ''}`}>
                  <i className={`${item.icon} text-base ${isActive ? 'text-accent-600' : 'text-foreground-400 group-hover:text-foreground-600'}`}></i>
                </div>
                <span className="truncate">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></div>
                )}
              </Link>
            );

            return acc;
          }, [])}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-background-100">
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-background-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-secondary-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{userInitial}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground-900 truncate">{displayName}</p>
              <p className="text-[10px] text-foreground-500 truncate">{user?.email || 'Sin correo'}</p>
            </div>
            <button
              onClick={() => setChangePwOpen(true)}
              aria-label="Cambiar mi contraseña"
              title="Cambiar mi contraseña"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors flex-shrink-0 cursor-pointer"
            >
              <i className="ri-lock-line text-sm"></i>
            </button>
            <button
              onClick={handleLogout}
              aria-label="Cerrar sesión"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0 cursor-pointer"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-background-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-line text-foreground-700 text-lg"></i>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-foreground-600">
              <Link to="/admin" className="hover:text-accent-600 transition-colors">Admin</Link>
              {currentSection && (
                <>
                  <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
                  <span className="text-foreground-400">{currentSection}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-background-200">
              <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{userInitial}</span>
              </div>
              <div className="leading-tight">
                <p className="text-xs font-semibold text-foreground-900">{displayName}</p>
                <p className="text-[10px] text-foreground-500 capitalize">{profile?.role || 'admin'}</p>
              </div>
            </div>
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((o) => !o)}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Notificaciones"
              >
                <i className="ri-notification-3-line text-foreground-600 text-lg"></i>
                {financingAlerts.length + reconciliationAlerts.length > 0 ? (
                  <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {financingAlerts.length + reconciliationAlerts.length > 9 ? '9+' : financingAlerts.length + reconciliationAlerts.length}
                  </span>
                ) : activities.length > 0 ? (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500"></span>
                ) : null}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-11 w-80 bg-white rounded-xl border border-background-200 overflow-hidden z-50">
                  {reconciliationAlerts.length > 0 && (
                    <div className="border-b border-amber-100 bg-amber-50">
                      <div className="flex items-center gap-2 px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-time-line text-amber-600"></i>
                        </div>
                        <p className="text-xs font-semibold text-amber-800 leading-snug">
                          {reconciliationAlerts.length} pago(s) en vuelo sin confirmar
                        </p>
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        {reconciliationAlerts.slice(0, 5).map((a) => {
                          const gatewayLabel = a.method === 'sistecredito' ? 'Sistecrédito' : 'ePayco';
                          const mins = Math.max(0, Math.round(a.minutesWaiting));
                          const waited = mins >= 60 ? `${Math.floor(mins / 60)} h` : `${mins} min`;
                          return (
                            <Link
                              key={`rec-${a.orderId}`}
                              to={`/admin/pedidos?order=${a.orderId}`}
                              onClick={() => setNotifOpen(false)}
                              className="block px-4 py-3 border-t border-amber-100 hover:bg-amber-100 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-amber-800 whitespace-nowrap">
                                  {a.orderNumber || 'Pedido'}
                                </span>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-amber-500 text-white">
                                  {gatewayLabel}
                                </span>
                              </div>
                              <p className="text-[11px] text-amber-700 mt-0.5">
                                Sin confirmar hace {waited}. Revisa el estado del pago con la pasarela.
                              </p>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {financingAlerts.length > 0 && (
                    <div className="border-b border-red-100 bg-red-50">
                      <div className="flex items-center gap-2 px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-close-circle-line text-red-600"></i>
                        </div>
                        <p className="text-xs font-semibold text-red-800 leading-snug">
                          {financingAlerts.length} pago(s) rechazado(s) por la pasarela
                        </p>
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        {financingAlerts.slice(0, 5).map((a) => {
                          const isSistecredito = a.method === 'sistecredito';
                          const gatewayLabel = isSistecredito ? 'Sistecrédito' : 'ePayco';
                          return (
                            <Link
                              key={`${a.orderId}:${a.method}`}
                              to={`/admin/pedidos?order=${a.orderId}`}
                              onClick={() => setNotifOpen(false)}
                              className="block px-4 py-3 border-t border-red-100 hover:bg-red-100 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-red-800 whitespace-nowrap">
                                  {a.orderNumber || 'Pedido'}
                                </span>
                                <span
                                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${isSistecredito ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}
                                >
                                  {gatewayLabel}
                                </span>
                              </div>
                              {a.customerName && (
                                <p className="text-[11px] text-foreground-600 mt-0.5 truncate">{a.customerName}</p>
                              )}
                              <p className="text-[11px] text-red-700 mt-0.5 line-clamp-2">
                                {a.reason || `${gatewayLabel} no registró un motivo específico.`}
                              </p>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                    <p className="text-sm font-semibold text-foreground-950">Actividad reciente</p>
                    <span className="text-[10px] text-foreground-400 uppercase tracking-wider">
                      Auditoría
                    </span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {activities.length === 0 ? (
                      <p className="px-4 py-6 text-center text-sm text-foreground-500">
                        Aún no hay actividad registrada.
                      </p>
                    ) : (
                      activities.map((a) => (
                        <div
                          key={a.id}
                          className="flex items-start gap-3 px-4 py-3 border-b border-background-50 hover:bg-background-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-history-line text-secondary-600"></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-foreground-900">
                              <span className="font-medium">{a.userName || 'Sistema'}</span>{' '}
                              {actionVerb(a.action)}{' '}
                              <span className="text-foreground-500">
                                {entityShortLabel(a.entityType)}
                              </span>
                            </p>
                            <p className="text-[10px] text-foreground-400 mt-0.5">
                              {formatRelative(a.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <Link
                    to="/admin/auditoria"
                    onClick={() => setNotifOpen(false)}
                    className="block text-center text-xs font-medium text-accent-600 hover:text-accent-700 py-2.5 border-t border-background-100 transition-colors"
                  >
                    Ver auditoría completa
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/"
              target="_blank"
              className="hidden sm:flex items-center gap-2 text-xs text-foreground-500 hover:text-accent-600 transition-colors px-3 py-1.5 rounded-lg border border-background-200 hover:border-accent-300 whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              Ver Tienda
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background-50">
          {children}
        </main>
      </div>

      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}
    </div>
  );
}